<template>
  <div class="modal-container" id="customModal" @click="event => closeModal(event)">
    <div class="modal-wrapper">

      <div class="modal-content">
        <div class="modal-sidebar">
          <div class="mb-3 mt-2">
            <logo width="30px"/>
          </div>

          <div class="modal-sidebar-link"
               @click="() => changePage(1)"
               :class="{'modal-sidebar-link--active': determineIfActive(1)}">
            <edit3-icon/>
          </div>

          <div class="modal-sidebar-link"
               @click="() => changePage(2)"
               :class="{'modal-sidebar-link--active': determineIfActive(2)}">
            <square-icon/>
          </div>

          <div class="modal-sidebar-link"
               @click="() => changePage(3)"
               :class="{'modal-sidebar-link--active': determineIfActive(3)}">
            <calendar-icon/>
          </div>

        </div>

        <div class="modal-tab-wrapper">

          <div class="modal-tab-header">
            <div>
              <h5 class="text-uppercase fw-bold text-green mb-2 mb-md-0">
                Search
              </h5>
            </div>

            <div class="d-flex align-items-center">
              <div class="btn btn-green btn-sm me-2" @click="searchArticles">Search</div>
              <div class="btn btn-ra-gray btn-sm me-2" @click="resetSearchOptions" data-bs-toggle="tooltip" data-bs-placement="bottom" title="Clear search criteria">Clear</div>
              <button type="button" class="btn-close" aria-label="Close" @click="hideModal"></button>
            </div>
          </div>

          <div class="modal-tab-content">
            <template v-if="page === 1">
              <h5>Keywords</h5>
              <or-includes/>
              <include/>
              <exclude/>
            </template>

            <template v-if="page === 2">
              <h5>Article Details</h5>
              <agencies/>
              <countries/>
              <states/>
              <sector-types/>
              <higher-order-alert-classification/>
              <!-- <sub-type-classification/> -->
              <alert-types/>
              <tags/>
              <classification/>
              <status/>
              <filing-type/>
              <in-federal-register/>
              <docket-file-number/>
              <order-notice-id/>
              <sec-release-number/>
              <federal-register-number/>
              <reg-alytics-alert-id/>

            </template>

            <template v-if="page === 3">
              <h5>Dates</h5>
              <latest-update/>
              <hr>
              <originally-published/>
              <hr>
              <proposed-comments-due/>
              <hr>
              <rule-effective/>
              <hr>
              <published-in-federal-reserve/>
            </template>

          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import Agencies from "@/components/articles/articles-search-options/Agencies";
import AlertTypes from "@/components/articles/articles-search-options/AlertTypes";
import Classification from "@/components/articles/articles-search-options/Classification";
import Countries from "@/components/articles/articles-search-options/Countries";
import DocketFileNumber from "@/components/articles/articles-search-options/DocketFileNumber";
import Exclude from "@/components/articles/articles-search-options/Exclude";
import FederalRegisterNumber from "@/components/articles/articles-search-options/FederalRegisterNumber";
import FilingType from "@/components/articles/articles-search-options/FilingType";
import HigherOrderAlertClassification from "@/components/articles/articles-search-options/HigherOrderAlertClassification.vue";
import InFederalRegister from "@/components/articles/articles-search-options/InFederalRegister";
import Include from "@/components/articles/articles-search-options/Include";
import LatestUpdate from "@/components/articles/articles-search-options/LatestUpdate";
import OrIncludes from "@/components/articles/articles-search-options/OrIncludes";
import OriginallyPublished from "@/components/articles/articles-search-options/OriginallyPublished";
import ProposedCommentsDue from "@/components/articles/articles-search-options/ProposedCommentsDue";
import PublishedInFederalReserve from "@/components/articles/articles-search-options/PublishedInFederalReserve";
import RegAlyticsAlertId from "@/components/articles/articles-search-options/RegAlyticsAlertId";
import RuleEffective from "@/components/articles/articles-search-options/RuleEffective";
import SecReleaseNumber from "@/components/articles/articles-search-options/SecReleaseNumber";
import SectorTypes from "@/components/articles/articles-search-options/SectorTypes";
import States from "@/components/articles/articles-search-options/States";
import Status from "@/components/articles/articles-search-options/Status";
import Tags from "@/components/articles/articles-search-options/Tags";
import OrderNoticeId from "@/components/articles/articles-search-options/orderNoticeId";
import { addOrUpdateParameter, resetUrlParams } from "@/helpers/urlParamsHelper";
import Logo from "@/layouts/components/Logo";
import { defaultSearchOptions, logSearchHistory } from "@/store/services/articleService";
import { CalendarIcon, Edit3Icon, SquareIcon } from 'vue-feather-icons';
import { mapActions, mapGetters } from "vuex";

export default {
  name: "ArticleAdvancedSearchModal",
  components: {
    PublishedInFederalReserve,
    RuleEffective,
    ProposedCommentsDue,
    OriginallyPublished,
    LatestUpdate,
    FederalRegisterNumber,
    RegAlyticsAlertId,
    DocketFileNumber,
    SecReleaseNumber,
    OrderNoticeId,
    Exclude,
    OrIncludes,
    Include,
    AlertTypes,
    SectorTypes,
    Tags,
    // SubTypeClassification,
    HigherOrderAlertClassification,
    Agencies,
    States,
    Countries,
    InFederalRegister,
    FilingType,
    Classification,
    Status,
    Logo,
    SquareIcon,
    CalendarIcon,
    Edit3Icon
  },
  computed: {
    ...mapGetters('search', ['getShowingModal', 'getSearchValue']),
  },

  data() {
    return {
      page: 1
    }
  },

  methods: {
    ...mapActions('search', ['getAllOptionsForAdvancedSearch']),

    closeModal(event) {
      if (event.target.id === 'customModal') {
        this.$store.dispatch('search/updateModalShowingState', false);
      }
    },

    changePage(pageNumber) {
      this.page = pageNumber;
    },

    // Determines if the current page is active based on the number provided
    determineIfActive(pageNumber) {
      return pageNumber === this.page;
    },

    // Resets the search options to the default state
    resetSearchOptions() {
      const parsedString = JSON.stringify(defaultSearchOptions);
      const clone = JSON.parse(parsedString);
      this.$store.dispatch('search/resetSearchFilters', clone);
      this.$store.dispatch("collections/setCollectionItem", null);

      // Resets the URL to the default route
      resetUrlParams();

      this.$store.dispatch('filters/resetAllSelectedFiltersSidebar');
      this.$store.dispatch('articles/getArticles');
      this.$store.dispatch('search/setSavedSearchName', '');
    },

    hideModal() {
      this.$store.dispatch('search/updateModalShowingState', false);
    },

    updateSearchNavigation(value) {
      this.$store.dispatch('search/updateSearchValue', value);
      // Adds the new value as parameters to the URI
      const encodedParams = encodeURI(value);
      addOrUpdateParameter("searchValue", encodedParams);
    },

    searchArticles() {
      this.hideModal();
      this.$store.dispatch('articles/setLoadingFromExternalSource', true);

      this.$store.dispatch('search/getSearchResults')
          .then(response => {
            // Builds the response in the format we require for the front-end
            // Required when using the materialized view
            let responseData = response.data.results;
            responseData.forEach((item, index) => {

              let alertType = ''

              if (item.alert_type) {
                alertType = item.alert_type;
              }

              responseData[index].alert_type = alertType;
            });
            this.$store.dispatch('articles/setArticlesFromExternalSource', responseData);
            this.$store.dispatch('articles/generateAndSetFilterOptions');
            this.$store.dispatch('articles/setLoadingFromExternalSource', false);
          })
          .then(() => {
            logSearchHistory();
          });
    },

  },

  mounted() {
    this.getAllOptionsForAdvancedSearch();
  }

}
</script>

<style lang="scss" scoped>
.modal-container {
  display: block;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  overflow: auto;

  background: rgba(0, 0, 0, 0.2);
  z-index: 9999;
}

.modal-wrapper {
  position: relative;
  border: none;
  border-radius: 0;
  outline: 0;
  background: white;
  margin: 0 0;
  width: 100% !important;
  height: 100%;

  @media (min-width: 991px) {
    border: 1px solid rgba(0,0,0,.2);
    border-radius: 10px;
    margin: 3% auto; /* 15% from the top and centered */
    width: 80%;
    max-width: 900px;
    height: fit-content;
  }


  /* Animate the content of the modal */
  -webkit-animation-name: animatetop;
  -webkit-animation-duration: 0.5s;
  animation-name: animatetop;
  animation-duration: 0.5s
}

.modal-content {
  display: flex;
  flex-direction: row;
  border-radius: 10px;

}

.modal-sidebar {
  border-radius: 10px 0 0 10px;
  width: 80px;
  min-height: 100vh;
  background: white;
  box-shadow: 0 15px 30px 0 rgba(0, 0, 0, 0.11), 0 5px 15px 0 rgba(0, 0, 0, 0.08) !important;

  @media (min-width: 991px) {
    min-height: 500px;
  }

  justify-content: center;
  text-align: center;

  padding: 10px 0;

}

.modal-sidebar-link {
  padding: 14px 5px;

  cursor: pointer;
  transition: 0.25s;

  &:hover {
    background-color: #eaeaea;
  }
}

.modal-sidebar-link--active {
  border-left: 2px solid #4a3288;
  background-color: #f8f8f8;
}

.modal-tab-wrapper {
  width: 100%;
  padding: 15px 20px;
  @media only screen and (max-width: 767px) {
    overflow: auto;
    max-height: 100vh;
}

}

.modal-tab-content {
  margin-top: 15px;
  @media only screen and (max-width: 767px) {
    overflow: auto;
    max-height: 100vh;
}

}

.modal-tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  background-color: white;
  //border: gainsboro 1px solid;

  @media(min-width: 768px) {
    flex-direction: row;
  }
}

.custom-searchbar {
  width: 100%;
  border: 0;
  padding: .4rem 0;
  font-size: 1.5rem;

  &:focus {
    border: none;
    outline: none;
  }
}

@-webkit-keyframes animatetop {
  from {top:-10px; opacity:0}
  to {top:0; opacity:1}
}

@keyframes animatetop {
  from {top:-10px; opacity:0}
  to {top:0; opacity:1}
}
</style>
